import moment from 'moment'
import { IOrder } from '../../../../app/entities/Order'
import { ITireRackOrder } from '../../../../app/entities/TireRackOrder'

/**
 * Get all tire rack order numbers for an order, including both
 * tracked (in tireRackOrders) and untracked (in additionalTireRackOrderNumber)
 */
export function getAllTireRackOrderNumbers(order: IOrder): string[] {
  const trackedTireRackOrderNumbers =
    (order.tireRackOrders
      ?.map((tro) => tro.orderNumber)
      .filter(Boolean) as string[]) || []
  const additionalTireRackOrderNumbers =
    order.additionalTireRackOrderNumber
      ?.split(/[,\s]+/) //split by comma or whitespace
      .map((n) => n.trim())
      .filter(
        (n) => n.length > 0 && !trackedTireRackOrderNumbers.includes(n),
      ) || []

  return [...trackedTireRackOrderNumbers, ...additionalTireRackOrderNumbers]
}

/**
 * Get only verified TireRackOrders
 */
export function getVerifiedTireRackOrders(order: IOrder): ITireRackOrder[] {
  return order.tireRackOrders?.filter((tro) => tro.verified) || []
}

/**
 * Format all tire rack order numbers for display
 */
export function formatTireRackOrderNumbers(order: IOrder): string {
  return getAllTireRackOrderNumbers(order).join(', ')
}

/**
 * Get the latest shipping arrival date from all verified TireRackOrders
 * Returns null if no verified TROs with valid shipping dates exist
 */
export function getLatestShippingArrival(order: IOrder | null): Date | null {
  if (!order) return null

  const verifiedTROs = getVerifiedTireRackOrders(order) //only verified TROs will have shipping data anyway, so we will get them alone
  if (!verifiedTROs.length) return null

  const validDates = verifiedTROs
    .map((tro) => tro.shippingEstimatedArrival)
    .filter((date) => date && date !== 'null' && moment(date).isValid())
    .map((date) => new Date(date))

  return validDates.length
    ? new Date(Math.max(...validDates.map((d) => d.getTime())))
    : null
}

/**
 * Get the primary verified TireRackOrder (first verified TRO)
 * Used for backward compatibility where single TRO is expected
 */
export function getPrimaryVerifiedTireRackOrder(
  order: IOrder | null,
): ITireRackOrder | null {
  if (!order) return null

  return getVerifiedTireRackOrders(order)[0] || null //this makes the assumption that the first verified TRO is the primary one. At the very least, it will be verified and have all the appropriate data on it
}

/**
 * Check if order has any tire rack orders (verified, unverified, or additional)
 */
export function hasTireRackOrders(order: IOrder): boolean {
  return (
    order.tireRackOrders?.length > 0 ||
    Boolean(order.additionalTireRackOrderNumber?.trim())
  )
}

/**
 * Get the latest delivered date from all verified TireRackOrders
 */
export function getLatestDeliveredDate(order: IOrder | null): Date | null {
  if (!order) return null

  const verifiedTROs = getVerifiedTireRackOrders(order)
  if (!verifiedTROs.length) return null

  const validDates = verifiedTROs
    .map((tro) => tro.shippingDeliveredOn)
    .filter((date) => date && moment(date).isValid())
    .map((date) => new Date(date))

  return validDates.length
    ? new Date(Math.max(...validDates.map((d) => d.getTime())))
    : null
}

/**
 * Get the latest stock ready time from all verified TireRackOrders
 */
export function getLatestStockReadyTime(order: IOrder | null): Date | null {
  if (!order) return null

  const verifiedTROs = getVerifiedTireRackOrders(order)
  if (!verifiedTROs.length) return null

  const validDates = verifiedTROs
    .map((tro) => tro.stockReadyTime)
    .filter((date): date is Date => moment(date).isValid())
    .map((date) => new Date(date))

  return validDates.length
    ? new Date(Math.max(...validDates.map((d) => d.getTime())))
    : null
}

/**
 * Get the latest stock become available time from all verified TireRackOrders
 */
export function getLatestStockBecomeAvailable(
  order: IOrder | null,
): Date | null {
  if (!order) return null

  const verifiedTROs = getVerifiedTireRackOrders(order)
  if (!verifiedTROs.length) return null

  const validDates = verifiedTROs
    .map((tro) => tro.stockBecameAvailable)
    .filter((date): date is Date => moment(date).isValid())
    .map((date) => new Date(date))

  return validDates.length
    ? new Date(Math.max(...validDates.map((d) => d.getTime())))
    : null
}

/**
 * Get the latest stock become available time from all verified TireRackOrders
 */
export function getLatestShippingShippedOn(order: IOrder | null): Date | null {
  if (!order) return null
  const verifiedTROs = getVerifiedTireRackOrders(order)
  if (!verifiedTROs.length) return null

  const validDates = verifiedTROs
    .map((tro) => tro.shippingShippedOn)
    .filter((date) => date && moment(date).isValid())
    .map((date) => new Date(date))

  return validDates.length
    ? new Date(Math.max(...validDates.map((d) => d.getTime())))
    : null
}
